import { type AIChatParams, AIProvider } from '@affine/core/blocksuite/ai';
import { extractSelectedContent } from '@affine/core/blocksuite/ai/utils/extract';
import type { AffineEditorContainer } from '@affine/core/blocksuite/block-suite-editor';
import { putAssistantDraft } from '@affine/core/desktop/pages/workspace/ask/assistant-handoff';
import { AuthService } from '@affine/core/modules/cloud';
import { WorkbenchService } from '@affine/core/modules/workbench/services/workbench';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useLiveData, useService } from '@toeverything/infra';
import { useEffect } from 'react';

import { useEnableAI } from './use-enable-ai';

export function useAskPipeHandoff(
  editor: AffineEditorContainer | null,
  onOpen?: () => void
) {
  const allowed = useEnableAI();
  const user = useLiveData(useService(AuthService).session.account$);
  const workbench = useService(WorkbenchService).workbench;
  const workspaceId = useService(WorkspaceService).workspace.id;
  useEffect(() => {
    if (!allowed || !user || !editor) return;
    let active = true;
    const open = async (params: AIChatParams | null) => {
      if (!params || params.host !== editor.host) return;
      // Consume first; a delayed extraction must not replay on another editor/account.
      AIProvider.slots.requestOpenWithChat.next(null);
      AIProvider.slots.requestSendWithChat.next(null);
      const context =
        params.context ??
        (await extractSelectedContent(params.host).catch(() => null));
      if (!active) return;
      const selection =
        context?.markdown ||
        context?.quote ||
        context?.combinedElementsMarkdown ||
        '';
      const text = [params.input, selection].filter(Boolean).join('\n\n');
      putAssistantDraft(user.id, workspaceId, text);
      onOpen?.();
      workbench.closeSidebar();
      workbench.open('/ask?mode=general');
    };
    const receive = (params: AIChatParams | null) => {
      open(params).catch(console.error);
    };
    const a = AIProvider.slots.requestOpenWithChat.subscribe(receive);
    const b = AIProvider.slots.requestSendWithChat.subscribe(receive);
    return () => {
      active = false;
      a.unsubscribe();
      b.unsubscribe();
    };
  }, [allowed, editor, onOpen, user, workbench, workspaceId]);
}
